import React, { useState, useContext } from 'react';
import { createUserWithEmailAndPassword, updateProfile, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
import { UserContext } from '../userContext';
import { useNavigate } from 'react-router-dom';
import { FaGoogle, FaCheckCircle } from 'react-icons/fa';
import './Cadastro.css';

function Cadastro() {
  const { setUser } = useContext(UserContext);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Nome é obrigatório.');
      return false;
    }
    if (formData.name.trim().length < 2) {
      setError('Nome deve ter pelo menos 2 caracteres.');
      return false;
    }
    if (!formData.email.trim()) {
      setError('E-mail é obrigatório.');
      return false;
    }
    if (formData.password.length < 6) {
      setError('Senha deve ter pelo menos 6 caracteres.');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Senhas não coincidem.');
      return false;
    }
    return true;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) return;

    setLoading(true);

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        formData.email, 
        formData.password
      );
      
      const userData = userCredential.user;

      await updateProfile(userData, {
        displayName: formData.name
      });

      await setDoc(doc(db, "usuarios", userData.uid), {
        name: formData.name,
        email: formData.email,
        plano: 'Free',
        createdAt: new Date(),
        provider: 'email'
      });

      setUser({
        name: formData.name,
        email: userData.email,
        photoURL: userData.photoURL,
        uid: userData.uid,
        plano: 'Free',
      });

      setSuccess('Conta criada com sucesso! Redirecionando...');
      
      setTimeout(() => {
        navigate('/');
      }, 2000);

    } catch (err) {
      console.error(err);
      if (err.code === 'auth/email-already-in-use') {
        setError('Este e-mail já está em uso. Tente fazer login.');
      } else if (err.code === 'auth/invalid-email') {
        setError('E-mail inválido. Verifique o formato.');
      } else if (err.code === 'auth/weak-password') {
        setError('Senha muito fraca. Use pelo menos 6 caracteres.');
      } else {
        setError('Erro ao criar conta. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleRegister = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const userData = result.user;

      const userDoc = await getDoc(doc(db, "usuarios", userData.uid));
      
      if (!userDoc.exists()) {
        await setDoc(doc(db, "usuarios", userData.uid), {
          name: userData.displayName,
          email: userData.email,
          plano: 'Free',
          createdAt: new Date(),
          provider: 'google'
        });
        
        setSuccess('Conta criada com sucesso! Redirecionando...');
      } else {
        setSuccess('Login realizado com sucesso! Redirecionando...');
      }

      setUser({
        name: userData.displayName || 'Usuário',
        email: userData.email,
        photoURL: userData.photoURL,
        uid: userData.uid,
        plano: userDoc.exists() ? userDoc.data().plano : 'Free',
      });

      setTimeout(() => {
        navigate('/');
      }, 2000);

    } catch (err) {
      console.error(err);
      setError('Erro no cadastro com Google. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const goToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="cadastro-container">
      <div className="cadastro-wrapper">
        {/* Coluna Esquerda - Informações */}
        <div className="cadastro-info">
          <h1>Junte-se à EAD Conect</h1>
          <p>
            Acesse centenas de materiais de estudo, conecte-se com outros estudantes 
            e acelere sua jornada acadêmica.
          </p>
          
          <ul className="features-list">
            <li>
              <div className="check-icon">
                <FaCheckCircle />
              </div>
              <span>Acesso gratuito por 3 dias</span>
            </li>
            <li>
              <div className="check-icon">
                <FaCheckCircle />
              </div>
              <span>Centenas de materiais de estudo</span>
            </li>
            <li>
              <div className="check-icon">
                <FaCheckCircle />
              </div>
              <span>Suporte acadêmico especializado</span>
            </li>
            <li>
              <div className="check-icon">
                <FaCheckCircle />
              </div>
              <span>Cancele quando quiser</span>
            </li>
          </ul>
        </div>

        {/* Coluna Direita - Formulário */}
        <div className="cadastro-form-container">
          <form onSubmit={handleRegister} className="cadastro-form">
            <h2>Criar sua conta</h2>
            <p className="cadastro-subtitle">É rápido e gratuito</p>
            
            {error && <div className="message error">{error}</div>}
            {success && <div className="message success">{success}</div>}
            
            <div className="input-group">
              <input
                type="text"
                name="name"
                placeholder="Seu nome completo"
                value={formData.name}
                onChange={handleInputChange}
                required
                disabled={loading}
              />
            </div>
            
            <div className="input-group">
              <input
                type="email"
                name="email"
                placeholder="Seu melhor e-mail"
                value={formData.email}
                onChange={handleInputChange}
                required
                disabled={loading}
              />
            </div>
            
            <div className="input-group">
              <input
                type="password"
                name="password"
                placeholder="Crie uma senha (mín. 6 caracteres)"
                value={formData.password}
                onChange={handleInputChange}
                required
                disabled={loading}
              />
            </div>
            
            <div className="input-group">
              <input
                type="password"
                name="confirmPassword"
                placeholder="Confirme sua senha"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                required
                disabled={loading}
              />
            </div>
            
            <button 
              type="submit" 
              className={`btn-primary ${loading ? 'btn-loading' : ''}`}
              disabled={loading}
            >
              {loading ? 'Criando conta...' : 'Criar minha conta'}
            </button>

            <div className="divider">
              <span>ou</span>
            </div>

            <button 
              type="button"
              className={`btn-google ${loading ? 'btn-loading' : ''}`}
              onClick={handleGoogleRegister}
              disabled={loading}
            >
              <FaGoogle />
              Cadastrar com Google
            </button>
            
            <div className="login-link">
              Já tem uma conta?
              <button 
                type="button"
                onClick={goToLogin}
                disabled={loading}
              >
                Fazer login
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Cadastro;